import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { RequireAuth, Shell } from './components/Layout';
import Login from './pages/Login';
import Fleet from './pages/Fleet';
import RobotPage from './pages/Robot';
import PhoneDrive, { DrivePicker } from './pages/PhoneDrive';
import { useT } from './i18n';
import { setNavigator } from './lib/nav';

const MapsPage = lazy(() => import('./pages/maps/MapsPage'));
const MediaPage = lazy(() => import('./pages/Media'));
const Agent = lazy(() => import('./pages/Agent'));
const SchedulePage = lazy(() => import('./pages/Schedule'));
const AutonomyPage = lazy(() => import('./pages/Autonomy'));
const RobotSensorsPage = lazy(() => import('./pages/RobotSensors'));
const AutonomyHistoryPage = lazy(() => import('./pages/AutonomyHistory'));
const UpdatesPage = lazy(() => import('./pages/Updates'));

function Loading() {
  const t = useT();
  return <div className="empty">{t('common.loading')}</div>;
}

const lazyEl = (el: React.ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    element: <RequireAuth />,
    children: [
      // Full-screen phone drive (no nav rail).
      { path: '/drive/:serial', element: <PhoneDrive /> },
      {
        element: <Shell />,
        children: [
          { index: true, element: <Navigate to="/fleet" replace /> },
          { path: '/fleet', element: <Fleet /> },
          { path: '/robots/:serial', element: <RobotPage /> },
          { path: '/robots/:serial/sensors', element: lazyEl(<RobotSensorsPage />) },
          { path: '/drive', element: <DrivePicker /> },
          { path: '/maps', element: lazyEl(<MapsPage />) },
          { path: '/maps/:mapId', element: lazyEl(<MapsPage />) },
          { path: '/media', element: lazyEl(<MediaPage />) },
          { path: '/media/:mediaId', element: lazyEl(<MediaPage />) },
          { path: '/agent', element: lazyEl(<Agent />) },
          { path: '/schedule', element: lazyEl(<SchedulePage />) },
          { path: '/autonomy', element: lazyEl(<AutonomyPage />) },
          { path: '/autonomy/history', element: lazyEl(<AutonomyHistoryPage />) },
          { path: '/autonomy/history/:sessionId', element: lazyEl(<AutonomyHistoryPage />) },
          { path: '/updates', element: lazyEl(<UpdatesPage />) },
          { path: '*', element: <Navigate to="/fleet" replace /> },
        ],
      },
    ],
  },
]);

setNavigator((path) => void router.navigate(path));

export default function App() {
  return <RouterProvider router={router} />;
}
